import { useEffect, useState } from "react";
import { Save, Loader2, Users, Clock, Truck } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ReviewBadge } from "@/components/ReviewBadge";

export default function ContentAdmin() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/site-content").then((r) => setForm(r.data)); }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/site-content", form); toast.success("Landing page updated"); }
    catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  if (!form) return <p className="text-slate-400">Loading…</p>;

  return (
    <div className="max-w-3xl">
      <h1 className="font-display font-extrabold text-3xl mb-1">Landing Content</h1>
      <p className="text-slate-400 mb-6">Edit the homepage headline, description and the three highlight badges. Changes go live instantly.</p>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-white/10 bg-[#131a29] p-5 space-y-4">
          <div>
            <label className="text-sm text-slate-400 mb-1.5 block">Headline (bold, large)</label>
            <Textarea data-testid="content-headline" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} className="bg-slate-950/50 border-white/10" />
          </div>
          <div>
            <label className="text-sm text-slate-400 mb-1.5 block">Description (smaller text)</label>
            <Textarea data-testid="content-description" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-slate-950/50 border-white/10" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="text-xs text-slate-400 mb-1 block">Badge 1</label><Input data-testid="content-badge1" value={form.badge1} onChange={(e) => setForm({ ...form, badge1: e.target.value })} className="bg-slate-950/50 border-white/10" /></div>
            <div><label className="text-xs text-slate-400 mb-1 block">Badge 2</label><Input data-testid="content-badge2" value={form.badge2} onChange={(e) => setForm({ ...form, badge2: e.target.value })} className="bg-slate-950/50 border-white/10" /></div>
            <div><label className="text-xs text-slate-400 mb-1 block">Badge 3</label><Input data-testid="content-badge3" value={form.badge3} onChange={(e) => setForm({ ...form, badge3: e.target.value })} className="bg-slate-950/50 border-white/10" /></div>
          </div>
          <div className="border-t border-white/10 pt-4 space-y-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={form.reviews_enabled !== false} onCheckedChange={(v) => setForm({ ...form, reviews_enabled: !!v })} data-testid="reviews-enabled" />
              Show the review badge on the landing page
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-400 mb-1 block">Title</label><Input data-testid="reviews-title" value={form.reviews_title || ""} onChange={(e) => setForm({ ...form, reviews_title: e.target.value })} className="bg-slate-950/50 border-white/10" /></div>
              <div><label className="text-xs text-slate-400 mb-1 block">Platform label</label><Input data-testid="reviews-platform" value={form.reviews_platform || ""} onChange={(e) => setForm({ ...form, reviews_platform: e.target.value })} className="bg-slate-950/50 border-white/10" /></div>
              <div><label className="text-xs text-slate-400 mb-1 block">Rating (1–5)</label><Input type="number" step="0.1" min="0" max="5" data-testid="reviews-rating" value={form.reviews_rating ?? ""} onChange={(e) => setForm({ ...form, reviews_rating: +e.target.value })} className="bg-slate-950/50 border-white/10" /></div>
              <div><label className="text-xs text-slate-400 mb-1 block">Review count (e.g. 12K)</label><Input data-testid="reviews-count" value={form.reviews_count || ""} onChange={(e) => setForm({ ...form, reviews_count: e.target.value })} className="bg-slate-950/50 border-white/10" /></div>
            </div>
          </div>
          <Button onClick={save} disabled={saving} data-testid="content-save" className="bg-violet-600 hover:bg-violet-500">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save changes
          </Button>
        </div>

        <div className="rounded-2xl border border-white/10 bg-gradient-to-b from-violet-950/30 to-slate-900/40 p-6">
          <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-3">Live preview</div>
          <h2 className="font-display font-extrabold tracking-tight text-3xl leading-[1.05]">{form.headline}</h2>
          <p className="mt-3 text-sm text-slate-300">{form.description}</p>
          <div className="mt-5 flex flex-wrap gap-4 text-sm text-slate-300">
            <span className="inline-flex items-center gap-2"><Users className="h-4 w-4 text-violet-400" /> {form.badge1}</span>
            <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-emerald-400" /> {form.badge2}</span>
            <span className="inline-flex items-center gap-2"><Truck className="h-4 w-4 text-amber-400" /> {form.badge3}</span>
          </div>
          {form.reviews_enabled !== false && (
            <div className="mt-6"><ReviewBadge title={form.reviews_title} rating={form.reviews_rating} count={form.reviews_count} platform={form.reviews_platform} /></div>
          )}
        </div>
      </div>
    </div>
  );
}
